import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { createAccountVisit, createNewAccount } from "../api.js";

type Props = {
  open: boolean;
  onClose: () => void;
  // The orphan kanban card that triggered this flow. Passed to the server
  // so the card gets re-pointed at the new account in the same txn and a
  // card.updated broadcast fires to every connected client.
  cardId: number;
  // Pre-fills — from the card's cluster contact info when the card is an
  // orphan lead (aid=0). Staff can edit before submitting.
  defaultFirstName?: string;
  defaultLastName?: string;
  defaultEmail?: string;
  defaultPhone?: string;
  // Called with the new vid once the full flow (account → blank visit)
  // completes, so the caller can open the new quote in QuoteEditor.
  onCreated: (newVid: number, newAid: number) => void;
};

export function CreateAccountModal({
  open,
  onClose,
  cardId,
  defaultFirstName = "",
  defaultLastName = "",
  defaultEmail = "",
  defaultPhone = "",
  onCreated,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const [firstName, setFirstName] = useState(defaultFirstName);
  const [lastName, setLastName] = useState(defaultLastName);
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate defaults whenever the modal opens — the card context might
  // change between openings (orphan card A → orphan card B).
  useEffect(() => {
    if (open) {
      setFirstName(defaultFirstName);
      setLastName(defaultLastName);
      setEmail(defaultEmail);
      setPhone(defaultPhone);
      setError(null);
    }
  }, [open, defaultFirstName, defaultLastName, defaultEmail, defaultPhone]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Allow blank name — we need at least one contact hook (name OR email
  // OR phone) to avoid creating totally empty records. Blank firstName is
  // coerced to "*" server-side; templates treat it as unknown and greet
  // with "Hi there".
  const hasAnyContactInfo =
    firstName.trim().length > 0 ||
    lastName.trim().length > 0 ||
    email.trim().length > 0 ||
    phone.trim().length > 0;
  const canSubmit = hasAnyContactInfo && !submitting;
  const nameMissing = firstName.trim().length === 0 && lastName.trim().length === 0;

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // Coerce blank first name to "*" so notify templates + server render
      // as unknown. Last name stays blank. This matches the PHP convention
      // (`* Unknown`, `* 07xxx`) that existing accounts use.
      const firstForServer = firstName.trim() || "*";
      const account = await createNewAccount({
        firstName: firstForServer,
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        cardId,
      });
      // Immediately spin up a blank visit on the brand-new account so
      // staff land in the quote editor with somewhere to add items.
      const visit = await createAccountVisit(account.aid, {
        cid: account.cid,
        lid: account.lid,
      });
      // Refresh the cluster so the Visits tab shows the new visit. The
      // board itself updates live via the card.updated SSE event the
      // server broadcasts after the re-link.
      await qc.invalidateQueries({ queryKey: ["cluster", cardId] });
      onCreated(visit.newVid, account.aid);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-[80] bg-ink-950/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="newacct"
            className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-3 md:p-8"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <div
              className="relative w-full max-w-xl rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="flex items-start justify-between gap-3 border-b border-ink-700 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-50">
                    Create New Account
                  </div>
                  <h3 className="mt-1 text-xl leading-tight text-ink-50">
                    {firstName || lastName
                      ? `${firstName} ${lastName}`.trim()
                      : "New customer"}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-3 py-1 text-[13px] text-ink-50 hover:border-ink-400"
                >
                  Close
                </button>
              </header>

              <div className="space-y-3 px-5 py-4 text-[13px]">
                <div className="grid grid-cols-2 gap-2">
                  <LabeledInput label="First name" value={firstName} onChange={setFirstName} />
                  <LabeledInput label="Last name" value={lastName} onChange={setLastName} />
                  <LabeledInput label="Email" value={email} onChange={setEmail} />
                  <LabeledInput label="Phone" value={phone} onChange={setPhone} />
                </div>
                <p className="text-[12px] text-ink-200">
                  Creates a new Breeze account with this contact + a blank location. You'll be able to fill in the service address on the quote.
                </p>
                {nameMissing && hasAnyContactInfo ? (
                  <p className="text-[12px] text-slack-yellow">
                    No name yet — saving as <span className="font-mono">*</span>. Outbound templates will greet the customer as &quot;Hi there&quot; until you fill the name in.
                  </p>
                ) : null}
              </div>

              <footer className="flex items-center justify-between gap-2 border-t border-ink-700 px-5 py-3">
                <div className="text-[13px]">
                  {error ? (
                    <span className="text-slack-pink">⚠ {error}</span>
                  ) : (
                    <span className="text-ink-200">
                      Opens a new quote for the fresh account.
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={onClose}
                    className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] text-ink-50 hover:border-ink-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => void handleSubmit()}
                    disabled={!canSubmit}
                    className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {submitting ? "Creating…" : "Create account"}
                  </button>
                </div>
              </footer>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] text-ink-200">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50"
      />
    </label>
  );
}
